/**
 * designer-layout — the ONE width rule the journey designers lay out against.
 *
 * Both designer shells (`AllJourneys`'s six tabs and `onestop-tab`) are a setup
 * rail beside a settings pane beside a customer preview. Their minimums add up
 * to about 1,100px before the Studio shell's own 64px icon rail, so on anything
 * narrower the row used to scroll SIDEWAYS: the whole designer moved under the
 * operator, the preview hung off the right edge, and the settings cards clipped
 * their own controls (operator, 2026-09-14 — the Launch & embed height row).
 *
 * Three layouts instead, and both shells read them from here so they cannot
 * drift apart:
 *
 *   wide  (>= 1180)  rail | settings | preview      — unchanged
 *   mid   (820-1179) rail | settings XOR preview    — a switch in the header
 *   phone (< 820)    settings XOR preview           — the rail becomes a picker
 *
 * The numbers are VIEWPORT width, not container width: these shells are
 * full-height chrome mounted directly in the Studio frame, and the panes they
 * size are siblings of the thing being measured.
 */
import { useEffect, useState } from "react";

export type DesignerLayout = "wide" | "mid" | "phone";

/** `rail` is where the three panes stop fitting side by side; `phone` is where
 *  the 236px setup rail stops leaving a readable pane beside it. */
export const DESIGNER_BP = { rail: 1180, phone: 820 }; // ui-config

export function designerLayoutFor(width: number): DesignerLayout {
  if (width < DESIGNER_BP.phone) return "phone";
  if (width < DESIGNER_BP.rail) return "mid";
  return "wide";
}

function read(): DesignerLayout {
  try { return designerLayoutFor(window.innerWidth); } catch { return "wide"; }
}

/** React hook form, for the hook-based shell (`onestop-tab`). The class-based
 *  shell reads the same rule through its own resize listener. */
export function useDesignerLayout(): DesignerLayout {
  const [layout, setLayout] = useState<DesignerLayout>(read);
  useEffect(() => {
    const on = () => setLayout(read());
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return layout;
}
